import { useCallback, useState } from "react";
import { FormProvider } from "react-hook-form";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Delete } from "@mui/icons-material";

import { Button, Form, FormCheckbox, Modal } from "@covertree/ui";
import FormFileField from "@covertree/ui/forms/FormFileField";
import { InformationBox } from "@covertree/ui/content";
import DocumentItem from "@covertree/ui/content/DocumentItem";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "@consts/index";
import { useFormWithNotification } from "@covertree/hooks";

import {
  UploadDocumentsResponse,
  useUploadPolicyDocumentsMutation,
} from "@redux/restApi/documentsApi";
import { defaultValues, UploadPolicyDocumentsForm, validator } from "./form";
import { useGetPolicyLazyQuery } from "@covertree/agent-portal-graphql";
import { Toast } from "@covertree/utils";

interface DocumentsUploadDialogProps {
  open: boolean;
  onClose: () => void;
  policyLocator: string;
}

export function DocumentsUploadDialog({
  open,
  onClose,
  policyLocator,
}: DocumentsUploadDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [uploadDocuments, { reset }] = useUploadPolicyDocumentsMutation();
  const [getPolicy] = useGetPolicyLazyQuery();

  const form = useFormWithNotification<UploadPolicyDocumentsForm>({
    defaultValues,
  });

  const {
    handleSubmit: submit,
    watch,
    control,
    reset: resetForm,
    setValue,
  } = form;

  const files = watch("files") || [];
  const isSignedManually = watch("signedManually") ?? false;

  const handleSubmit = useCallback(
    async (payload: UploadPolicyDocumentsForm) => {
      setIsLoading(true);

      const results = await Promise.all(
        payload.files.map((file) =>
          uploadDocuments({
            file,
            policyLocator,
            isSignedManually,
          })
        )
      );

      const errorResult = results.find((result) => "error" in result);
      const anySuccessResult = results.some((result) => !("error" in result));

      if (errorResult) {
        setError(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (errorResult.error?.data as UploadDocumentsResponse)?.error ||
            "Unknown error"
        );
      }
      if (anySuccessResult) {
        const response = await getPolicy({
          variables: { policyLocator },
          fetchPolicy: "network-only",
        });
        if (response?.data?.getPolicy.documents) {
          const failedDocuments = payload.files.filter(
            (file) =>
              !response.data.getPolicy.documents.find(
                (doc) => doc.fileName === file.name
              )
          );
          if (!failedDocuments.length) {
            handleClose();

            if (payload.signedManually) {
              Toast.showSuccess("Policy has been signed and marked as Active.");
            }
          } else {
            setValue("files", failedDocuments);
          }
        }
      }
      setIsLoading(false);
    },
    [policyLocator, isSignedManually, uploadDocuments]
  );

  const handleClose = useCallback(() => {
    setError(undefined);
    reset();
    resetForm();
    onClose();
  }, [onClose, reset, resetForm]);

  const removeDocument = useCallback(
    (documentName: string) => {
      setValue(
        "files",
        files.filter((file) => documentName !== file.name)
      );
    },
    [files, setValue]
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Upload policy documents"
      maxWidth="md"
      disableClose={isLoading}>
      <Form onSubmit={submit(handleSubmit)}>
        <FormProvider {...form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {error && (
                <Typography fontSize="1rem" color="error">
                  Something went wrong during upload of documents:
                  <Typography
                    component="span"
                    fontSize="inherit"
                    fontWeight="700">
                    {" "}
                    {error}
                  </Typography>
                  . Please try again
                </Typography>
              )}
              <FormFileField
                name="files"
                accept={ACCEPTED_FILE_TYPES}
                maxFileSize={MAX_FILE_SIZE}
                maxFileAmount={10}
                disabled={isLoading}
                validator={validator}
              />
            </Grid>

            <Grid item xs={12}>
              <FormCheckbox
                id="manually-signed-checkbox"
                dataTestId="manually-signed-checkbox"
                name="signedManually"
                control={control}
                disabled={isLoading}
                label={
                  <Typography variant="subtitle2" fontWeight={300}>
                    Is this document a Signed Policy Application?
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <InformationBox sx={{ color: "text.primary" }}>
                <Typography variant="subtitle2" fontWeight={300}>
                  Please note that when this document is uploaded, no
                  notification will be sent to inform Customer Support about the
                  upload.
                </Typography>
              </InformationBox>
            </Grid>

            {files.map((file: File) => (
              <Grid item xs={12} key={file.name}>
                <DocumentItem
                  name={file.name}
                  actions={[
                    {
                      name: "Remove",
                      onClick: () => removeDocument(file.name),
                      disabled: isLoading,
                      icon: <Delete />,
                    },
                  ]}
                />
              </Grid>
            ))}
            <Grid item container spacing={2}>
              <Grid item>
                <Button
                  variant="contained"
                  type="submit"
                  loading={isLoading}
                  disabled={!files.length || isLoading}>
                  Submit
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isLoading}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </FormProvider>
      </Form>
    </Modal>
  );
}
